import { useState } from 'react';
import { getStripeUrl, STRIPE_PRODUCTS } from '../lib/supabase';

export default function CreditsModal({ teamId, currentCredits, onClose }) {
  const [loading, setLoading] = useState(null);

  const handlePurchase = async (bundle) => {
    setLoading(bundle.name);
    const url = await getStripeUrl(bundle.name, teamId);
    if (url) {
      window.open(url, '_blank');
    }
    setLoading(null);
  };

  const fmt$ = (n) => '$' + n.toLocaleString();

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={styles.eyebrow}>The LAB Sampling Platform</div>
            <h2 style={styles.title}>Purchase Sampling Credits</h2>
            <p style={styles.sub}>
              Each credit = one complete statistical sampling engagement,
              including sample draw, scoring, and PDF report.
              {' '}<strong>Current balance: {currentCredits} credit{currentCredits !== 1 ? 's' : ''}</strong>
            </p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Bundle cards */}
        <div style={styles.bundles}>
          {STRIPE_PRODUCTS.map(bundle => (
            <div
              key={bundle.name}
              style={{
                ...styles.bundleCard,
                ...(bundle.highlight ? styles.bundleHighlight : {}),
              }}
            >
              {bundle.highlight && (
                <div style={styles.popularBadge}>Most Popular</div>
              )}
              <div style={styles.bundleName}>{bundle.name}</div>
              <div style={styles.bundleCredits}>
                {bundle.credits} credit{bundle.credits !== 1 ? 's' : ''}
              </div>
              <div style={styles.bundlePrice}>{fmt$(bundle.price)}</div>
              <div style={styles.bundlePerCredit}>
                {fmt$(bundle.perCredit)} per credit
              </div>
              {bundle.savings && (
                <div style={styles.bundleSavings}>{bundle.savings}</div>
              )}
              <button
                style={{
                  ...styles.purchaseBtn,
                  ...(bundle.highlight ? styles.purchaseBtnHighlight : {}),
                }}
                onClick={() => handlePurchase(bundle)}
                disabled={loading === bundle.name}
              >
                {loading === bundle.name ? 'Opening…' : 'Purchase →'}
              </button>
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={styles.footer}>
          <span style={styles.lockIcon}>🔒</span>
          Secure payment via Stripe · Credits never expire · Applied instantly to your team
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(13, 36, 97, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '860px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 32px 80px rgba(0,0,0,0.3)',
  },
  header: {
    padding: '36px 36px 24px',
    borderBottom: '1.5px solid #e8ecf5',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '24px',
  },
  eyebrow: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#F5A800',
    marginBottom: '6px',
  },
  title: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '24px',
    color: '#0D2461',
    margin: '0 0 8px',
  },
  sub: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#5a6a8a',
    lineHeight: 1.6,
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#8a96b0',
    cursor: 'pointer',
    padding: '4px 8px',
    flexShrink: 0,
  },
  bundles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    padding: '28px 36px',
  },
  bundleCard: {
    border: '1.5px solid #c8d0e0',
    borderRadius: '8px',
    padding: '24px 20px',
    textAlign: 'center',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  bundleHighlight: {
    border: '2px solid #0D2461',
    boxShadow: '0 4px 20px rgba(13,36,97,0.12)',
  },
  popularBadge: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#0D2461',
    color: '#F5A800',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    padding: '3px 12px',
    borderRadius: '20px',
    whiteSpace: 'nowrap',
  },
  bundleName: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '13px',
    color: '#0D2461',
  },
  bundleCredits: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '28px',
    fontWeight: 600,
    color: '#0D2461',
  },
  bundlePrice: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '22px',
    color: '#0D2461',
  },
  bundlePerCredit: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#8a96b0',
  },
  bundleSavings: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    fontWeight: 600,
    color: '#1a5c3a',
    background: '#e4f2eb',
    borderRadius: '20px',
    padding: '3px 10px',
    display: 'inline-block',
  },
  purchaseBtn: {
    marginTop: '8px',
    padding: '10px',
    background: '#f4f6fb',
    color: '#0D2461',
    border: '1.5px solid #c8d0e0',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer',
  },
  purchaseBtnHighlight: {
    background: '#F5A800',
    color: '#0D2461',
    border: '1.5px solid #F5A800',
  },
  footer: {
    padding: '16px 36px 24px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#8a96b0',
    textAlign: 'center',
    borderTop: '1px solid #e8ecf5',
  },
  lockIcon: { marginRight: '6px' },
};
